/**
 * Central auth token / session flags.
 *
 * Default: Bearer token in localStorage (current backend contract).
 * Optional: set REACT_APP_AUTH_USE_HTTPONLY_COOKIE=true when the API sets
 * an HttpOnly session cookie on login — then no token is stored in JS and
 * axios sends credentials (withCredentials) instead of Authorization.
 */

const TOKEN_KEY = 'accessToken'
const REFRESH_TOKEN_KEY = 'refreshToken'
const ROLE_KEY = 'ROLE'
const SUPER_KEY = 'SUPER'
const IMPERSONATOR_KEY = 'IMPERSONATOR_ID'

export const AUTH_USE_HTTPONLY_COOKIE =
    process.env.REACT_APP_AUTH_USE_HTTPONLY_COOKIE === 'true'

export function getAccessToken() {
    if (AUTH_USE_HTTPONLY_COOKIE) return null
    try {
        return window.localStorage.getItem(TOKEN_KEY)
    } catch {
        return null
    }
}

export function setAccessToken(token) {
    if (!token) {
        clearAccessToken()
        return
    }
    if (AUTH_USE_HTTPONLY_COOKIE) return
    window.localStorage.setItem(TOKEN_KEY, token)
}

export function clearAccessToken() {
    try {
        window.localStorage.removeItem(TOKEN_KEY)
    } catch {
        /* ignore */
    }
}

export function getRefreshToken() {
    if (AUTH_USE_HTTPONLY_COOKIE) return null
    try {
        return window.localStorage.getItem(REFRESH_TOKEN_KEY)
    } catch {
        return null
    }
}

export function setRefreshToken(token) {
    if (!token) {
        clearRefreshToken()
        return
    }
    if (AUTH_USE_HTTPONLY_COOKIE) return
    window.localStorage.setItem(REFRESH_TOKEN_KEY, token)
}

export function clearRefreshToken() {
    try {
        window.localStorage.removeItem(REFRESH_TOKEN_KEY)
    } catch {
        /* ignore */
    }
}

export function getRole() {
    try {
        return window.localStorage.getItem(ROLE_KEY)
    } catch {
        return null
    }
}

export function setRole(role) {
    if (role == null) return
    window.localStorage.setItem(ROLE_KEY, role)
}

export function clearRole() {
    try {
        window.localStorage.removeItem(ROLE_KEY)
    } catch {
        /* ignore */
    }
}

export function setSuperAdminMode(enabled) {
    if (enabled) {
        window.localStorage.setItem(SUPER_KEY, 'true')
    } else {
        window.localStorage.removeItem(SUPER_KEY)
    }
}

export function isSuperAdminMode() {
    return window.localStorage.getItem(SUPER_KEY) === 'true'
}

export function setImpersonatorId(id) {
    if (!id) {
        clearImpersonatorId()
        return
    }
    window.localStorage.setItem(IMPERSONATOR_KEY, String(id))
}

export function getImpersonatorId() {
    try {
        return window.localStorage.getItem(IMPERSONATOR_KEY)
    } catch {
        return null
    }
}

export function clearImpersonatorId() {
    try {
        window.localStorage.removeItem(IMPERSONATOR_KEY)
    } catch {
        /* ignore */
    }
}

/** Headers for manual Axios calls (prefer default axios + interceptors). */
export function getAuthHeaders() {
    const token = getAccessToken()
    if (!token) return {}
    return { authorization: `Bearer ${token}` }
}

export function clearAuthStorage() {
    clearAccessToken()
    clearRefreshToken()
    clearRole()
    setSuperAdminMode(false)
    clearImpersonatorId()
}
